import type { LlmModel, LlmModelSelectDto, LlmStreamMetadataDto } from '@@types/api.types';
import { LlmStreamStatus } from '@@types/api.types';
import { useChat } from '@ai-sdk/react';
import { LlmStreamMetadataSchema } from '@azymmetric/shared/schemas/llm.schema';
import { LlmUIMessage as LlmChatMessage } from '@azymmetric/shared/types/llm.types';
import { LlmChatModal } from '@components/shared/llm-chat/llm-chat-modal.component';
import { type PromptGuidelines, PromptPopover } from '@components/shared/prompt-popover.component';
import { Button } from '@components/ui/button';
import { useBuildHeaders } from '@hooks/api.hook';
import { errorToast } from '@hooks/toast.hook';
import { cn } from '@lib/utils';
import { llmModelStore } from '@stores/llm-model.store';
import { DefaultChatTransport } from 'ai';
import { Loader2Icon, SparklesIcon } from 'lucide-react';
import React, { ReactNode } from 'react';
import { useStore } from 'zustand';

export type GenerateButtonProps = {
  apiEndpoint: string;
  /**
   * Chat endpoint for the fullscreen LlmChatModal workspace. Providing it together
   * with `onAccept` makes Maximize open the chat instead of the legacy fullscreen.
   */
  chatApiEndpoint?: string;
  className?: string;
  /**
   * Data to send with the request (e.g., fieldName, technologyId, etc.)
   */
  data: Record<string, unknown>;
  description?: ReactNode;
  disabled?: boolean;
  /**
   * Accept a Suggestion produced in the fullscreen chat workspace: receives the
   * accepted draft content and writes it into the target form field.
   */
  onAccept?: (value: string) => void;
  onStream: (value: string) => void;
  /**
   * Callback when generation metadata is available
   */
  onMetadata?: (metadata: LlmStreamMetadataDto) => void;
  /**
   * Callback when streaming starts
   */
  onStreamingStart?: () => void;
  /**
   * Callback when streaming ends
   */
  onStreamingEnd?: () => void;
  placeholder?: string;

  /**
   * Optional prompt guidelines for enhanced UX
   */
  information?: PromptGuidelines;
  /**
   * Optional model filter function
   */
  modelFilter?: (model: LlmModelSelectDto) => boolean;
  /**
   * Optional title for the prompt popover
   */
  title?: string;
};

export const GenerateButton: React.FC<GenerateButtonProps> = (props) => {
  const {
    apiEndpoint,
    chatApiEndpoint,
    className,
    data,
    description,
    disabled = false,
    information,
    modelFilter,
    onAccept,
    onMetadata,
    onStream,
    onStreamingStart,
    onStreamingEnd,
    placeholder,
    title,
  } = props;
  const { buildHeaders } = useBuildHeaders();
  const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
  const [isChatOpen, setIsChatOpen] = React.useState(false);
  const { selectedModel } = useStore(llmModelStore);
  const [currentText, setCurrentText] = React.useState('');
  const [prompt, setPrompt] = React.useState('');
  const onStreamingEndRef = React.useRef<typeof onStreamingEnd>(undefined);
  const previousStatusRef = React.useRef<'error' | 'ready' | 'streaming' | 'submitted'>('ready');

  const { messages, sendMessage, status, stop } = useChat<LlmChatMessage>({
    transport: new DefaultChatTransport({
      api: apiEndpoint,
      credentials: 'include',
      headers: async () => await buildHeaders(),
    }),
    onError: () => {
      errorToast({ description: 'Failed to generate content' });
    },
    onData: (dataPart) => {
      if (dataPart.type === 'data-custom-metadata') {
        const validatedMetadata = LlmStreamMetadataSchema.safeParse(dataPart.data);
        if (validatedMetadata.success) {
          const { status, ...rest } = validatedMetadata.data;
          // Map the parsed status string onto the generated DTO enum so the whole
          // metadata chain uses a single representation (LlmStreamMetadataDto).
          onMetadata?.({
            ...rest,
            status: status === 'loading' ? LlmStreamStatus.Loading : LlmStreamStatus.Completed,
          });
        }
      }
    },
  });

  const isLoading = status === 'streaming' || status === 'submitted';

  React.useEffect(() => {
    onStreamingEndRef.current = onStreamingEnd;
  }, [onStreamingEnd]);

  const submitMessage = async () => {
    if (isLoading) {
      return;
    }

    setIsPopoverOpen(false);
    setCurrentText('');
    onStreamingStart?.();

    await sendMessage(
      { text: prompt },
      {
        body: {
          ...data,
          ...(selectedModel && { model: selectedModel }),
          prompt,
        },
      },
    );
    setPrompt('');
  };

  React.useEffect(() => {
    const latestAssistantMessage = messages.findLast((m) => m.role === 'assistant');
    if (latestAssistantMessage) {
      const text = latestAssistantMessage.parts
        .filter((part) => part.type === 'text')
        .map((part) => part.text)
        .join('');

      if (text !== currentText) {
        setCurrentText(text);
        onStream(text);
      }
    }
  }, [messages, currentText, onStream]);

  const handlePromptSubmit = (data: { model?: LlmModel; prompt?: string }) => {
    return submitMessage();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (!isLoading) {
        submitMessage();
      }
    }
  };

  React.useEffect(() => {
    const previousStatus = previousStatusRef.current;
    const wasLoading = previousStatus === 'streaming' || previousStatus === 'submitted';
    const isSettled = status === 'ready' || status === 'error';
    const shouldNotifyStreamingEnd = wasLoading && isSettled;

    if (shouldNotifyStreamingEnd) {
      onStreamingEndRef.current?.();
    }

    previousStatusRef.current = status;
  }, [status]);

  const handleOpenChat = () => {
    setIsPopoverOpen(false);
    setIsChatOpen(true);
  };

  const handleChatClose = () => {
    setIsChatOpen(false);
    // The typed prompt was handed off to the chat; reopening the popover starts fresh.
    setPrompt('');
  };

  return (
    <>
      <PromptPopover
        prompt={prompt}
        setPrompt={setPrompt}
        description={description}
        information={information}
        isLoading={isLoading}
        isOpen={isPopoverOpen}
        onExpand={chatApiEndpoint && onAccept ? handleOpenChat : undefined}
        onOpenChange={setIsPopoverOpen}
        modelFilter={modelFilter}
        onKeyDown={handleKeyDown}
        onSubmit={handlePromptSubmit}
        placeholder={placeholder}
        stop={stop}
        title={title}
        trigger={
          isLoading ? (
            <Button
              className={cn('hover:text-[#3D2EFF] z-10', className)}
              onClick={() => stop()}
              size="xs"
              variant="secondary"
            >
              <span className="animate-pulse flex items-center gap-1">
                <Loader2Icon className="animate-spin" />
                Stop
              </span>
            </Button>
          ) : (
            <Button
              className={cn('size-7 min-w-7 text-gray-400 hover:text-[#3D2EFF] hover:bg-[#e0e0f7]', className)}
              disabled={disabled || isLoading}
              size="icon"
              variant="ghost"
            >
              <SparklesIcon />
            </Button>
          )
        }
      />

      {chatApiEndpoint && onAccept && (
        <LlmChatModal
          apiEndpoint={chatApiEndpoint}
          data={data}
          description={description}
          information={information}
          initialInput={prompt}
          isOpen={isChatOpen}
          modelFilter={modelFilter}
          onAccept={onAccept}
          onClose={handleChatClose}
          title={title}
        />
      )}
    </>
  );
};
